import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Public Privacy Policy page — clean reading layout.
 * Content is maintained inline so it's version-controlled with the code.
 */
const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-300 hover:text-white"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-white font-semibold">Privacy Policy</h1>
        </div>
      </div>

      {/* Content */}
      <article
        className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 leading-relaxed"
        data-testid="privacy-policy-content"
      >
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
          Mobile Messaging &amp; SMS Privacy Policy
        </h1>
        <p className="text-sm text-slate-400 mb-8">
          Fin &amp; Feathers (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is committed to protecting your privacy. This section describes how we collect and handle information for our reservation-by-text and mobile messaging services.
        </p>

        <section className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-3">1. Information We Collect</h2>
          <p>
            When you interact with our reservation system—either by submitting our online reservation form or by texting us directly at (702) 546-6394—we collect your mobile phone number, name, and any reservation details you provide (such as party size, date, time, seating preferences, and email address). We also collect your name, mobile phone number, and email address when you voluntarily sign up for our &quot;Join Our Loyalty Program&quot; form located on our homepage.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-3">2. How We Use Your Information</h2>
          <p>
            We use your mobile information exclusively to process, pre-qualify, coordinate, and confirm your restaurant reservation requests, as well as to collect mandatory large-party deposit information. We do not use your reservation phone number for unsolicited promotional text marketing campaigns. For members of our Loyalty Program, we use your information to send exclusive rewards, birthday offers, special event invitations, and promotional updates. You will only receive promotional marketing messages if you explicitly opt-in via our homepage loyalty form.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-3">
            3. Protection of SMS Opt-In Data (Carrier Compliance)
          </h2>
          <p>
            We deeply value your privacy. Your mobile information, SMS opt-in data, and consent details—for both reservations and our Loyalty Program—will strictly NOT be shared, sold, rented, or leased to any third parties, affiliates, or data brokers for marketing or promotional purposes under any circumstances.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-3">4. Your Choices and Opt-Out</h2>
          <p>
            You can cancel the SMS service at any time. Just text &quot;STOP&quot; to (702) 546-6394. After you send the SMS message &quot;STOP&quot; to us, we will send you an SMS message to confirm that you have been unsubscribed. After this, you will no longer receive SMS messages from us unless you initiate a new reservation request. If you want to join again, just sign up as you did the first time or text us a new reservation request.
          </p>
        </section>

        <div className="pt-6 border-t border-slate-800 text-sm text-slate-500">
          See also our{' '}
          <button
            onClick={() => navigate('/terms-of-service')}
            className="text-red-400 hover:text-red-300 underline"
            data-testid="privacy-link-terms"
          >
            Terms of Service
          </button>
          .
        </div>
      </article>
    </div>
  );
};

export default PrivacyPolicyPage;
